// Markdown preview renderer — port of the reference `mdToHtml` (design/unpacked/task-panes-v6/logic.split.js L44–71,
// inventory F14). Same block/inline rules, the same regular expressions applied in the same order, and the same
// inline styles; but instead of concatenating HTML strings (innerHTML) it builds a typed tree that is rendered as
// Vue VNodes. Text is never parsed as HTML, so no raw markup can be injected (ADR 0011 decision 7).
//
// Intentional differences from the prototype (documented in the report / design acceptance notes):
// - Links are only emitted for http:, https: and mailto: (the prototype also allowed "#"), and use
//   rel="noopener noreferrer". Anything else is rendered as its link text.
// - A line that the paragraph loop refuses but no block rule accepts (e.g. "# a<U+2028>b") is consumed as a
//   one-line paragraph instead of looping forever.
import { h, type FunctionalComponent, type VNode } from 'vue';
import { s as sDefault } from '@/app/templateHelpers';
import { rtlStyle } from '@/styles/bidi';
import { t } from '@/app/i18n';

export type Inline =
  | { t: 'text'; v: string }
  | { t: 'code' | 'strong' | 'em' | 's'; c: Inline[] }
  | { t: 'a'; href: string; c: Inline[] };

export type Block =
  | { t: 'pre'; text: string }
  | { t: 'h'; level: number; first: boolean; c: Inline[] }
  | { t: 'hr' }
  | { t: 'quote'; lines: Inline[][] }
  | { t: 'list'; ordered: boolean; items: Array<{ task: null | boolean; c: Inline[] }> }
  | { t: 'p'; lines: Inline[][] };

// ---------------------------------------------------------------------------------------------------------------
// Inline: the prototype runs regex replacements over an escaped string. We run the very same regexes over the raw
// string, emitting private sentinel markers (C0 controls the server never accepts in content) instead of tags, then
// turn the marker string into a tree. Markers: \u0001K = open K, \u0002K = close K, links carry an index into a side
// table: \u0001a<idx>\u0003 … \u0002a.
const O = '\u0001', C = '\u0002', SEP = '\u0003';
const DELIM: Record<string, string> = { c: '`', b: '**', e: '*', s: '~~' };
const SAFE_URL = /^(https?:|mailto:)/i;

/** Rebuilds the source text of a fragment that already went through earlier inline passes (used for hrefs). */
function unmark(x: string): string {
  return x.replace(/[\u0001\u0002]([cbes])/g, (_m, k: string) => DELIM[k] ?? '');
}

function markInline(src: string, links: string[]): string {
  // Strip anything that could be confused with our markers (never valid in saved content anyway).
  const x = src.replace(/[\u0001-\u0003]/g, '');
  return x
    .replace(/`([^`]+)`/g, O + 'c$1' + C + 'c')
    .replace(/\*\*([^*]+)\*\*/g, O + 'b$1' + C + 'b')
    .replace(/(^|[^*])\*([^*]+)\*/g, '$1' + O + 'e$2' + C + 'e')
    .replace(/~~([^~]+)~~/g, O + 's$1' + C + 's')
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, text: string, url: string) => {
      const href = unmark(url);
      if (!SAFE_URL.test(href)) return text;
      links.push(href);
      return O + 'a' + (links.length - 1) + SEP + text + C + 'a';
    });
}

const TAG: Record<string, 'code' | 'strong' | 'em' | 's' | 'a'> = { c: 'code', b: 'strong', e: 'em', s: 's', a: 'a' };

/** Parses one line of inline Markdown into a tree (exported for tests). */
export function parseInline(src: string): Inline[] {
  const links: string[] = [];
  const m = markInline(src, links);
  const root: Inline[] = [];
  const stack: Array<{ k: string; node: Inline & { c: Inline[] } }> = [];
  const cur = () => (stack.length ? stack[stack.length - 1]!.node.c : root);
  let buf = '';
  const flush = () => { if (buf) { const c = cur(); const last = c[c.length - 1]; if (last && last.t === 'text') last.v += buf; else c.push({ t: 'text', v: buf }); buf = ''; } };
  for (let i = 0; i < m.length; i++) {
    const ch = m[i];
    if (ch === O) {
      flush();
      const k = m[++i]!;
      let node: Inline & { c: Inline[] };
      if (k === 'a') {
        const end = m.indexOf(SEP, i + 1);
        const idx = Number(m.slice(i + 1, end));
        i = end;
        node = { t: 'a', href: links[idx] ?? '', c: [] };
      } else node = { t: TAG[k] as 'code' | 'strong' | 'em' | 's', c: [] };
      cur().push(node);
      stack.push({ k, node });
    } else if (ch === C) {
      flush();
      const k = m[++i]!;
      // Mis-nested markers (possible with overlapping regex matches) close like the HTML parser would: pop to the
      // matching open element; a close without an open is ignored.
      const at = stack.map(e => e.k).lastIndexOf(k);
      if (at >= 0) stack.length = at;
    } else buf += ch;
  }
  flush();
  return root;
}

// ---------------------------------------------------------------------------------------------------------------
// Blocks: verbatim port of the prototype's line loop.
const RE_FENCE = /^```/;
const RE_HEAD = /^(#{1,6})\s+(.*)$/;
// Prototype: /^\s*([-*_])\s*\1\s*\1[\s\1]*$/ — inside a character class `\1` is the octal escape \x01 (not a
// back-reference), so "----" is NOT a rule there. Kept identical on purpose.
const RE_HR = /^\s*([-*_])\s*\1\s*\1[\s\x01]*$/;
const RE_QUOTE = /^>\s?/;
const RE_LIST = /^\s*([-*+]|\d+\.)\s+/;
const RE_OL = /^\s*\d+\./;
const RE_TASK = /^\[( |x)\]\s+(.*)$/i;
const RE_PARA_STOP = /^(#{1,6}\s|>|```|\s*([-*+]|\d+\.)\s+)/;

export function parseMarkdown(src: string | null | undefined): Block[] {
  const lines = (src || '').replace(/\r/g, '').split('\n');
  const out: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const l = lines[i]!;
    if (RE_FENCE.test(l)) {
      const buf: string[] = []; i++;
      while (i < lines.length && !RE_FENCE.test(lines[i]!)) buf.push(lines[i++]!);
      i++;
      out.push({ t: 'pre', text: buf.join('\n') });
      continue;
    }
    let m: RegExpMatchArray | null;
    if ((m = l.match(RE_HEAD))) { out.push({ t: 'h', level: m[1]!.length, first: out.length === 0, c: parseInline(m[2]!) }); i++; continue; }
    if (RE_HR.test(l)) { out.push({ t: 'hr' }); i++; continue; }
    if (RE_QUOTE.test(l)) {
      const buf: Inline[][] = [];
      while (i < lines.length && RE_QUOTE.test(lines[i]!)) buf.push(parseInline(lines[i++]!.replace(RE_QUOTE, '')));
      out.push({ t: 'quote', lines: buf });
      continue;
    }
    if (RE_LIST.test(l)) {
      const ordered = RE_OL.test(l), items: Array<{ task: null | boolean; c: Inline[] }> = [];
      while (i < lines.length && RE_LIST.test(lines[i]!)) {
        const it = lines[i++]!.replace(RE_LIST, '');
        const tk = it.match(RE_TASK);
        items.push(tk ? { task: tk[1] !== ' ', c: parseInline(tk[2]!) } : { task: null, c: parseInline(it) });
      }
      out.push({ t: 'list', ordered, items });
      continue;
    }
    if (!l.trim()) { i++; continue; }
    const buf: Inline[][] = [];
    while (i < lines.length && lines[i]!.trim() && !RE_PARA_STOP.test(lines[i]!)) buf.push(parseInline(lines[i++]!));
    if (!buf.length) buf.push(parseInline(lines[i++]!)); // see header: the prototype would loop forever here
    out.push({ t: 'p', lines: buf });
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Rendering (styles verbatim from mdToHtml).
const CODE = 'font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-size:0.88em; background:var(--n34353a, #34353a); border-radius:4px;';
const H_SIZE = [0, 19, 16, 14, 13, 13, 12];

type StyleFn = (css: string) => string;

function inl(nodes: Inline[], accent: string, s: StyleFn): Array<VNode | string> {
  return nodes.map(n => {
    switch (n.t) {
      case 'text': return n.v;
      case 'code': return h('code', { style: s(CODE + ' padding:1px 5px;') }, inl(n.c, accent, s));
      case 'a': return h('a', { href: n.href, target: '_blank', rel: 'noopener noreferrer', style: s('color:' + accent + ';') }, inl(n.c, accent, s));
      default: return h(n.t, inl(n.c, accent, s));
    }
  });
}

function joinBr(lines: Inline[][], accent: string, s: StyleFn): Array<VNode | string> {
  const out: Array<VNode | string> = [];
  lines.forEach((ln, i) => { if (i) out.push(h('br')); out.push(...inl(ln, accent, s)); });
  return out;
}

/** Renders Markdown source to VNodes for the preview column. `s` mirrors styles for RTL like every other view. */
export function renderMarkdown(src: string | null | undefined, accent: string, emptyText = t('Nothing to preview.'), s: StyleFn = sDefault): VNode[] {
  const blocks = parseMarkdown(src);
  if (!blocks.length) return [h('p', { style: s('margin:0; color:var(--n76777d, #76777d);') }, emptyText)];
  return blocks.map(b => {
    switch (b.t) {
      case 'pre': return h('pre', { dir: 'ltr', style: s(CODE + ' padding:10px 12px; overflow-x:auto; margin:0 0 12px; white-space:pre;') }, [h('code', b.text)]);
      case 'h': return h('div', { role: 'heading', 'aria-level': b.level, style: s('font-size:' + H_SIZE[b.level] + 'px; font-weight:700; margin:' + (b.first ? 0 : 18) + 'px 0 8px; line-height:1.3;') }, inl(b.c, accent, s));
      case 'hr': return h('hr', { style: s('border:none; border-top:1px solid var(--n3a3b40, #3a3b40); margin:16px 0;') });
      case 'quote': return h('div', { style: s('border-left:3px solid var(--n45464b, #45464b); padding:2px 0 2px 12px; margin:0 0 12px; color:var(--n9c9da3, #9c9da3);') }, joinBr(b.lines, accent, s));
      case 'list': return h(b.ordered ? 'ol' : 'ul', { style: s('margin:0 0 12px; padding-left:22px;') }, b.items.map(it => it.task === null
        ? h('li', { style: s('margin-bottom:3px;') }, inl(it.c, accent, s))
        : h('li', { style: s('list-style:none; margin-left:-20px; margin-bottom:3px; display:flex; gap:8px; align-items:baseline;') }, [
          h('span', { role: 'img', 'aria-label': it.task ? t('Checked') : t('Not checked'), style: s('flex-shrink:0; width:13px; height:13px; border-radius:3px; border:1.5px solid ' + (it.task ? accent : 'var(--n6d6e74, #6d6e74)') + '; background:' + (it.task ? accent : 'transparent') + '; position:relative; top:2px;') }),
          h('span', it.task ? { style: s('color:var(--n8a8b91, #8a8b91); text-decoration:line-through;') } : {}, inl(it.c, accent, s)),
        ])));
      case 'p': return h('p', { style: s('margin:0 0 12px;') }, joinBr(b.lines, accent, s));
    }
  });
}

// Production addition: the preview follows the file's own writing direction, not the UI language, so a Persian
// file reads right-to-left in an English UI and vice versa. Majority of strong letters outside code decides.
const RTL_CHAR = /[֐-ࣿיִ-﷿ﹰ-﻿]/g;
const LTR_CHAR = /[A-Za-zÀ-ɏͰ-ϿЀ-ӿ]/g;

/** Writing direction of Markdown source: 'rtl' when right-to-left letters outnumber left-to-right ones. */
export function contentDir(src: string | null | undefined): 'rtl' | 'ltr' {
  const text = (src ?? '').replace(/```[\s\S]*?(```|$)/g, '').replace(/`[^`\n]*`/g, '').replace(/\]\([^)]*\)/g, ']');
  const r = text.match(RTL_CHAR)?.length ?? 0, l = text.match(LTR_CHAR)?.length ?? 0;
  return r > l ? 'rtl' : 'ltr';
}

/** Style function mirroring the reference's physical styles for the given content direction. */
export const dirStyle = (dir: 'rtl' | 'ltr'): StyleFn => (dir === 'rtl' ? rtlStyle : (css: string) => css);

/** Functional preview component: `<MarkdownPreview :src="text" :accent="accent" :dir="dir" />`. */
export const MarkdownPreview: FunctionalComponent<{ src: string; accent: string; dir?: 'rtl' | 'ltr' }> = p =>
  renderMarkdown(p.src, p.accent, undefined, p.dir ? dirStyle(p.dir) : sDefault);
MarkdownPreview.props = ['src', 'accent', 'dir'];
